import * as React from "react";
import type { AuthenticatedUser } from "@dosa/types";
import type { RequestOtpInput, VerifyOtpInput } from "@dosa/validation";
import { getCurrentUser } from "../lib/api-client";
import { fetchMe, realLogout, requestOtp, verifyOtpAndLogin } from "../lib/auth-api";

/**
 * On mount, this checks the REAL session first (GET /api/auth/me against the Worker — see
 * apps/worker/src/auth/routes.ts) via `fetchMe`. `requestOtp`/`verifyOtp` are the real
 * Authentica OTP login flow and work whenever a Worker is reachable at VITE_API_BASE_URL,
 * in any environment.
 *
 * `loginAsDemoRole` is a separate, local-only fallback for reviewing the UI with no Worker
 * running at all — it never touches the network. `import.meta.env.DEV` is compiled to a
 * literal `false` by Vite in a production build (`vite build`), so this guard is not a
 * runtime toggle an attacker can flip — the dead branch is eliminated at build time.
 * LoginPage additionally hides the role-switcher UI behind the same check.
 */

const DEMO_USER_IDS = {
  PATIENT: "usr-demo-patient-1",
  PHARMACIST: "usr-demo-pharmacist-1",
  ADMIN: "usr-demo-admin-1",
} as const;

interface AuthContextValue {
  user: AuthenticatedUser | null;
  isLoading: boolean;
  requestOtp: (input: RequestOtpInput) => Promise<void>;
  verifyOtp: (input: VerifyOtpInput) => Promise<void>;
  loginAsDemoRole: (role: keyof typeof DEMO_USER_IDS) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = React.createContext<AuthContextValue | undefined>(undefined);
const DEMO_STORAGE_KEY = "dosa_demo_user_id";

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = React.useState<AuthenticatedUser | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);

  React.useEffect(() => {
    (async () => {
      try {
        const realUser = await fetchMe();
        if (realUser) {
          setUser(realUser);
          return;
        }
      } catch {
        // No Worker reachable (e.g. pure local demo review) — fall through to demo state.
      }
      const demoId = localStorage.getItem(DEMO_STORAGE_KEY);
      if (demoId) setUser(await getCurrentUser(demoId));
    })().finally(() => setIsLoading(false));
  }, []);

  const handleRequestOtp = React.useCallback(async (input: RequestOtpInput) => {
    await requestOtp(input);
  }, []);

  const handleVerifyOtp = React.useCallback(async (input: VerifyOtpInput) => {
    const verifiedUser = await verifyOtpAndLogin(input);
    setUser(verifiedUser);
  }, []);

  const loginAsDemoRole = React.useCallback(async (role: keyof typeof DEMO_USER_IDS) => {
    if (!import.meta.env.DEV) {
      throw new Error("Demo role sign-in is only available in local development.");
    }
    const id = DEMO_USER_IDS[role];
    const u = await getCurrentUser(id);
    setUser(u);
    if (u) localStorage.setItem(DEMO_STORAGE_KEY, id);
  }, []);

  const logout = React.useCallback(async () => {
    await realLogout();
    setUser(null);
    localStorage.removeItem(DEMO_STORAGE_KEY);
  }, []);

  return (
    <AuthContext.Provider
      value={{ user, isLoading, requestOtp: handleRequestOtp, verifyOtp: handleVerifyOtp, loginAsDemoRole, logout }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = React.useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
}
